import React, {forwardRef, memo, useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {createSelector} from 'reselect';
import {batchActions} from 'redux-batched-actions';

import modClasses from '@mapsight/ui/src/js/helpers/mod-classes';
import {createListFeatureSelector} from '@mapsight/core/lib/list/selectors';
import {FEATURE_LIST, FEATURE_SOURCES} from '@mapsight/ui/src/js/config/constants/controllers';
import {viewSelector} from '@mapsight/ui/src/js/store/selectors';
import {getAllFeatures} from '@mapsight/core/lib/feature-sources/selectors';

import {useMagic, useNavPos} from '../../../hooks';
import {isDefined, unreachable} from '../../../modules/helpers';

import {setLanguageOpen, setNavigationOpen} from '../../store/actions';
import {languageOpenSelector} from '../../store/selectors';

import Link from '../link';


export default memo(forwardRef(
	/** @param {{
	 * 	other?: boolean,
	 * 	hideTabIndex?: boolean,
	 * 	tabRef: React.MutableRefObject<HTMLElement | undefined>,
	 * }} props */
	function LangSwitcher({other, hideTabIndex, tabRef}, ref) {
		const dispatch = useDispatch();
		const navPos = useNavPos();
		const langs = useGetLangs();
		const open = useSelector(languageOpenSelector);
		const toggleOpen = useCallback(
			() =>
				dispatch(batchActions([
					setNavigationOpen(false),
					setLanguageOpen(!open)
				])),
			[open]
		);

		const moveFocus = useCallback(
			() => tabRef.current && tabRef.current.focus(),
			[tabRef]
		);


		if (langs.length < 2) {
			return null;
		}

		const currLang = navPos.app?.lang;

		const content = !other ?
			(<button
				className="stade-lang-switcher__current"
				onClick={toggleOpen}
				tabIndex={hideTabIndex ? -1 : 0}
				ref={ref}

				aria-controls="stade-lang-switcher"
				aria-expanded={open}
			>
				{currLang && (
					<LangSwitcherEntry
						lang={currLang}
						minimizeOnMobile
					/>
				)}
			</button>) :
			(<div className={modClasses("stade-lang-switcher__other", {open})}>
				{langs.filter((l) => l.lang !== currLang).map((l) => (
					<Link
						to={l.url}
						className="stade-lang-switcher__other__link"
						key={l.url}
						external={true}
						tabIndex={hideTabIndex ? -1 : undefined}
						ref={ref}
					>
						<LangSwitcherEntry lang={l.lang} />
					</Link>
				))}
			</div>);

		return (
			// FIXME make this work like a button
			<div id="stade-lang-switcher" className="stade-lang-switcher">
				{content}

				{tabRef.current && <button
					aria-hidden={'true'}
					className="visuallyhidden"
					onFocus={moveFocus}
					tabIndex={hideTabIndex || !open? -1 : undefined}
				/>}
			</div>
		);
	}
));

const LangSwitcherEntry = memo(
	/** @param {{
	 *   lang: import('../../types').Lang,
	 *   minimizeOnMobile?: boolean,
	 * }} props */
	function LangSwitcherEntry({ lang, minimizeOnMobile = false }) {
		const view = useSelector(viewSelector);

		const text = (() => {
			switch (lang) {
				case 'en': return 'English';
				case 'de': return 'Deutsch';
				default: return unreachable(lang);
			}
		})();

		return (
			<span className="stade-lang-switcher__entry">
				<span className={modClasses('stade-lang-switcher__entry__flag', lang)} />

				{(!minimizeOnMobile || view === 'desktop' || view === 'fullscreen') && (
					<span className="stade-lang-switcher__entry__text">
						{text}
					</span>
				)}
			</span>
		);
	}
);

const featuresSelector = createSelector(
	createListFeatureSelector(FEATURE_LIST, FEATURE_SOURCES),
	/** @returns {Array<import('../../types').Feature>} */
	(featuresSources) => getAllFeatures(featuresSources),
);

function useGetLangs() {
	const navPos = useNavPos();
	const magic = useMagic();
	const features = useSelector(featuresSelector);

	const navLang = navPos.app?.lang;

	if (!isDefined(navLang) || !isDefined(magic.apps)) {
		return [];
	}

	return magic.apps
		.filter((app) => app.urls[navLang] === navPos.app?.url)
		.map((app) => {
			const suffix = (() => {
				if (navPos.list && app.lang === 'de') {
					return '/liste';
				} else if (navPos.list && app.lang === 'en') {
					return '/list';
				} else if (navPos.listMorePoi && app.lang === 'de') {
					return '/poi-liste';
				} else if (navPos.listMorePoi && app.lang === 'en') {
					return '/poi-list';
				} else if (navPos.map && app.lang === 'de') {
					return '/karte';
				} else if (navPos.map && app.lang === 'en') {
					return '/map';
				} else if (navPos.home && app.lang === 'de') {
					return '';
				} else if (navPos.home && app.lang === 'en') {
					return '';
				} else if (navPos.deepLink?.length) {
					const feat = features
						.find((feat) => feat.properties.ids[navLang] === navPos.deepLink);
					if (feat === undefined) {
						return null;
					}
					return `/${feat.properties.ids[app.lang]}`;
				} else {
					return null;
				}
			})();

			if (!isDefined(suffix)) {
				return null;
			}

			return {
				lang: app.lang,
				url: `${app.url}${suffix}`,
			};
		})
		.filter(isDefined);
}
